import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, X, ArrowRight } from "lucide-react";
import { API, authHeaders } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";

export default function BillingSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("pending"); // pending | paid | failed | expired
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    async function poll() {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, { headers: authHeaders() });
        if (cancelled) return;
        setInfo(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts++ >= 30) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus("failed");
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-xl items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="dc-card w-full p-8 text-center" data-testid="billing-success-page">
        {status === "pending" && (
          <>
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-[#ff4458]" />
            <h1 className="font-display text-2xl font-bold text-white">{t("paymentPending")}</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#32d583]/15">
              <Check className="h-8 w-8 text-[#32d583]" />
            </div>
            <h1 className="font-display text-3xl font-bold text-white" data-testid="payment-success-title">{t("paymentSuccess")}</h1>
            <p className="mt-2 text-sm text-[#a8adbd]">{t("paymentSuccessMessage")}</p>
            {info?.entitlements && (
              <div className="mt-5 inline-flex gap-3 rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3 text-sm text-white">
                <span>{t("creditsBalance")}: <b className="dc-gradient-text">{info.entitlements.credits}</b></span>
                {info.entitlements.subscription_active && (
                  <span>· {t("subscriptionUntil")}: {new Date(info.entitlements.subscription_until).toLocaleDateString()}</span>
                )}
              </div>
            )}
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button onClick={() => navigate("/analyze/premium")} data-testid="go-premium-analyze" className="inline-flex items-center justify-center gap-2 rounded-full dc-gradient-bg px-5 py-3 font-display text-sm font-semibold text-white">
                {t("startPremium")}<ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => navigate("/dashboard")} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#222636] bg-[#181b24] px-5 py-3 text-sm font-semibold text-white">
                {t("backToDashboard")}
              </button>
            </div>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#fd267a]/15">
              <X className="h-8 w-8 text-[#fd267a]" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white" data-testid="payment-failed-title">{t("paymentFailed")}</h1>
            <button onClick={() => navigate("/premium")} className="mt-6 inline-flex items-center justify-center gap-2 rounded-full dc-gradient-bg px-5 py-3 font-display text-sm font-semibold text-white">
              {t("goToPremium")}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
